import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, ReceiptText, LogOut, Settings, UserRound } from "lucide-react";
import { toast } from "sonner";
import { AriaMark } from "@/components/AriaMark";
import { PersonAvatar } from "@/components/PersonAvatar";
import avaPhoto from "@/assets/ava-thompson.png";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Chip } from "@/components/StatusChip";
import { api, timeAgo, fmtUSD, STATUS_META } from "@/lib/api";

const TITLES = {
  "/expenses": "Expenses",
  "/policies": "Policies",
  "/integrations": "Integrations",
  "/communications": "Communications",
  "/employees": "Employee chat",
  "/settings": "Settings",
};

const PAGES = [
  { to: "/expenses", label: "Expenses" },
  { to: "/expenses?mode=archive", label: "Archive" },
  { to: "/policies", label: "Policies" },
  { to: "/integrations", label: "Integrations" },
  { to: "/communications", label: "Communications" },
  { to: "/settings", label: "Settings" },
];

export const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [query, setQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [expenseHits, setExpenseHits] = useState([]);
  const debounceRef = useRef(null);

  const title =
    TITLES[location.pathname] ||
    TITLES["/" + (location.pathname.split("/")[1] || "")] ||
    "Aria";

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    api.get("/dashboard/activity?limit=6").then((r) => setNotifications(r.data)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    if (cmdOpen && employees.length === 0) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    }
    if (!cmdOpen) { setQuery(""); setExpenseHits([]); }
  }, [cmdOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setExpenseHits([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/expenses?q=${encodeURIComponent(query.trim())}&tab=all&page_size=5`)
        .then((r) => setExpenseHits(r.data.items || []))
        .catch(() => setExpenseHits([]));
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const q = query.trim().toLowerCase();
  const pageHits = PAGES.filter((p) => !q || p.label.toLowerCase().includes(q));
  const employeeHits = q
    ? employees.filter((e) =>
        e.name.toLowerCase().includes(q) || e.department.toLowerCase().includes(q) || (e.role || "").toLowerCase().includes(q)
      ).slice(0, 5)
    : [];

  const go = (to) => { navigate(to); setCmdOpen(false); };

  return (
    <header className="z-20 shrink-0 border-b border-[#222222] bg-[rgba(10,10,10,0.72)] backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center gap-4 px-4 sm:px-6 lg:px-8">
      <h1 className="text-[15px] font-semibold tracking-tight" data-testid="topnav-page-title">{title}</h1>

      <div className="ml-auto flex items-center gap-2">
        <button
          data-testid="topnav-global-search-button"
          onClick={() => setCmdOpen(true)}
          className="flex h-8 w-8 items-center justify-center gap-2 rounded-xl border border-[#222222] bg-[#111111] px-0 text-[12px] text-[#A3A3A3] hover:border-[#333] sm:w-56 sm:justify-start sm:px-3"
        >
          <Search size={13} />
          <span className="hidden flex-1 text-left sm:inline">Search…</span>
          <kbd className="hidden rounded border border-[#2a2a2a] bg-[#0A0A0A] px-1.5 py-px text-[10px] text-[#6b6b6b] sm:inline">⌘K</kbd>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topnav-notifications-button"
              className="relative flex h-8 w-8 items-center justify-center rounded-xl border border-[#222222] bg-[#111111] text-[#A3A3A3] hover:text-white hover:border-[#333]"
            >
              <Bell size={14} />
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#4F8EF7]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 border-[#222] bg-[#111] text-white">
            <DropdownMenuLabel className="flex items-center gap-2 text-white">
              <AriaMark size={14} /> Aria activity
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#222]" />
            <div className="max-h-72 overflow-y-auto py-1">
              {notifications.map((n) => (
                <div key={n.id} className="px-2 py-1.5 mx-1 rounded-lg hover:bg-[#1a1a1a]">
                  <div className="text-[12px] leading-snug text-[#e5e5e5]">{n.text}</div>
                  <div className="mt-0.5 text-[10px] text-[#6b6b6b]">{timeAgo(n.ts)}</div>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topnav-profile-pill"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-[#2a2a2a] bg-[#1c1c1c] text-[11px] font-semibold text-white transition-colors hover:border-[#3a3a3a]"
            >
              AT
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 border-[#222] bg-[#111] text-white" data-testid="topnav-profile-menu">
            <div className="flex items-center gap-3 px-2.5 py-2.5">
              <img src={avaPhoto} alt="Ava Thompson" className="h-9 w-9 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-white" data-testid="topnav-profile-name">Ava Thompson</div>
                <div className="truncate text-[11px] text-[#A3A3A3]">Controller · Finance</div>
                <div className="truncate text-[10.5px] text-[#6b6b6b]">ava.thompson@acme.com</div>
              </div>
            </div>
            <DropdownMenuSeparator className="bg-[#222]" />
            <DropdownMenuItem data-testid="topnav-profile-view" onSelect={() => navigate("/settings?tab=team")}
              className="gap-2 text-[12.5px] focus:bg-[#1c1c1c] focus:text-white">
              <UserRound size={14} className="text-[#7FAEF9]" /> View profile
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="topnav-profile-settings" onSelect={() => navigate("/settings")}
              className="gap-2 text-[12.5px] focus:bg-[#1c1c1c] focus:text-white">
              <Settings size={14} className="text-[#A3A3A3]" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#222]" />
            <DropdownMenuItem data-testid="topnav-logout"
              onSelect={() => toast.info("Signed out", { description: "Demo mode — your session stays active." })}
              className="gap-2 text-[12.5px] text-[#F87171] focus:bg-[#1c1c1c] focus:text-[#F87171]">
              <LogOut size={14} /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen} commandProps={{ shouldFilter: false }}>
        <CommandInput placeholder="Search employees, expenses, pages…" data-testid="command-palette-input"
          value={query} onValueChange={setQuery} />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {employeeHits.length > 0 && (
            <CommandGroup heading="Employees" data-testid="command-group-employees">
              {employeeHits.map((e) => (
                <CommandItem key={e.id} value={`emp-${e.id}`} data-testid={`command-employee-${e.id}`}
                  onSelect={() => go(`/employees/${e.id}`)} className="gap-2.5">
                  <PersonAvatar name={e.name} size={22} color={e.avatar_color} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px]">{e.name}</span>
                    <span className="block truncate text-[10.5px] text-[#6b6b6b]">{e.role} · {e.department}</span>
                  </span>
                  <span className="text-[11px] tabular-nums text-[#6b6b6b]">{fmtUSD(e.monthly_spend)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {expenseHits.length > 0 && (
            <CommandGroup heading="Expenses" data-testid="command-group-expenses">
              {expenseHits.map((e) => (
                <CommandItem key={e.id} value={`exp-${e.id}`} data-testid={`command-expense-${e.id}`}
                  onSelect={() => go(`/expenses?q=${encodeURIComponent(e.merchant)}`)} className="gap-2.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#1a1a1a] text-[#7FAEF9]"><ReceiptText size={12} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px]">{e.merchant} · {fmtUSD(e.amount)}</span>
                    <span className="block truncate text-[10.5px] text-[#6b6b6b]">{e.employee_name || "Unassigned"} · {e.category}</span>
                  </span>
                  <Chip tone={STATUS_META[e.status]?.tone || "neutral"}>{STATUS_META[e.status]?.label || e.status}</Chip>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {(employeeHits.length > 0 || expenseHits.length > 0) && <CommandSeparator />}
          <CommandGroup heading="Pages" data-testid="command-group-pages">
            {pageHits.map((p) => (
              <CommandItem
                key={p.to}
                value={`page-${p.label}`}
                data-testid={`command-item-${p.label.toLowerCase()}`}
                onSelect={() => go(p.to)}
              >
                {p.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </header>
  );
};
